import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInventory,
  selectFilteredInventory,
  selectInventoryFilters,
  selectInventoryLoading,
  selectInventoryError,
  setFilters,
  clearFilters
} from '../store/slices/inventorySlice';
import { selectSeason } from '../store/slices/seasonSlice';
import {
  Package,
  MapPin,
  Search,
  X,
  AlertCircle,
  AlertTriangle,
  Plus,
  PackagePlus,
  PackageMinus,
  Filter,
  Upload,
  Printer
} from 'lucide-react';
import AddInventoryModal from '../components/InventoryModals/AddInventoryModal';
import ReduceInventoryModal from '../components/InventoryModals/ReduceInventoryModal';
import CorrectDeficitModal from '../components/InventoryModals/CorrectDeficitModal';
import BulkUploadModal from '../components/InventoryModals/BulkUploadModal';
import PrintInventoryModal from '../components/Inventory/PrintInventoryModal';
import { Can } from '../components/Auth/Can';

const Inventory = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const items = useSelector(selectFilteredInventory);
  const filters = useSelector(selectInventoryFilters);
  const loading = useSelector(selectInventoryLoading);
  const error = useSelector(selectInventoryError);
  const season = useSelector(selectSeason);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isReduceModalOpen, setIsReduceModalOpen] = useState(false);
  const [isCorrectDeficitModalOpen, setIsCorrectDeficitModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeficitOnly, setShowDeficitOnly] = useState(false);

  // Calculate deficit count and total
  const deficitCount = items.filter(item => item.cantidad < 0).length;
  const totalDeficit = items
    .filter(item => item.cantidad < 0)
    .reduce((sum, item) => sum + Math.abs(item.cantidad), 0);

  const locationOptions = [
    {
      value: 'all',
      label: t('inventory.locationAll'),
      description: t('inventory.locationAllDescription')
    },
    {
      value: 'Campo',
      label: t('inventory.locationHuerta'),
      description: t('inventory.locationHuertaDescription')
    },
    {
      value: 'Warehouse USA',
      label: t('inventory.locationWarehouse'),
      description: t('inventory.locationWarehouseDescription')
    }
  ];

  useEffect(() => {
    const fetchParams = {};
    if (season?.season?.id && season?.week) {
      fetchParams.temporada_id = season.season.id;
      fetchParams.semana = season.week;
    }
    dispatch(fetchInventory(fetchParams));
  }, [dispatch, season]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
    if (key === 'location') {
      const fetchParams = { location: value !== 'all' ? value : undefined };
      if (season?.season?.id && season?.week) {
        fetchParams.temporada_id = season.season.id;
        fetchParams.semana = season.week;
      }
      dispatch(fetchInventory(fetchParams));
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    const fetchParams = {};
    if (season?.season?.id && season?.week) {
      fetchParams.temporada_id = season.season.id;
      fetchParams.semana = season.week;
    }
    dispatch(fetchInventory(fetchParams));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    // Return format YYYY-MM-DD
    return dateString.split('T')[0];
  };

  const getStockStatus = (cantidad) => {
    if (cantidad < 0) return 'deficit';
    if (cantidad === 0) return 'empty';
    return 'normal';
  };

  const handleAdd = (item) => {
    setSelectedStock(item);
    setIsAddModalOpen(true);
  };

  const handleReduce = (item) => {
    setSelectedStock(item);
    setIsReduceModalOpen(true);
  };

  const handleCorrect = (item) => {
    setSelectedStock(item);
    setIsCorrectDeficitModalOpen(true);
  };

  // Apply deficit filter
  const displayedItems = showDeficitOnly 
    ? items.filter(item => item.cantidad < 0)
    : items;

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('inventory.title')}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            {t('inventory.subtitle')}
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setIsPrintModalOpen(true)}
            title="Imprimir listado de inventario"
          >
            <Printer size={18} />
            Imprimir
          </button>
          <Can I="bulk" this="inventory:management">
            <button
              className="btn btn-outline btn-bulk-upload"
              onClick={() => setIsBulkUploadModalOpen(true)}
              title="Carga masiva desde Excel"
            >
              <Upload size={18} />
            </button>
          </Can>
          <Can I="add" this="inventory:management">
            <button
              className="btn btn-primary"
              onClick={() => {
                setSelectedStock(null);
                setIsAddModalOpen(true);
              }}
            >
              <Plus size={20} />
              {t('inventory.addStock')}
            </button>
          </Can>
        </div>
      </div>

      {deficitCount > 0 && (
        <div className="deficit-summary-card" onClick={() => setShowDeficitOnly(true)}>
          <div className="deficit-summary-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="deficit-summary-content">
            <div className="deficit-summary-title">
              {deficitCount} {deficitCount === 1 ? 'Producto' : 'Productos'} en Déficit
            </div>
            <div className="deficit-summary-subtitle">
              Total en déficit: {totalDeficit} cajas
            </div>
            <div className="deficit-summary-action">
              Haga clic para ver productos que requieren corrección →
            </div>
          </div>
        </div>
      )}

      <div className="card location-selector-card">
        <div className="location-selector-header">
          <MapPin size={18} />
          <span>{t('inventory.selectLocation')}</span>
        </div>
        <div className="location-selector-buttons">
          {locationOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`location-button ${filters.location === option.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('location', option.value)}
            >
              <div className="location-button-icon">
                <MapPin size={18} />
              </div>
              <div className="location-button-text">
                <span className="location-label">{option.label}</span>
                <span className="location-description">{option.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder={t('inventory.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-actions">
            <button 
              className={`btn btn-outline ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={20} />
              Filtros
            </button>

            <button 
              className={`btn ${showDeficitOnly ? 'btn-warning active' : 'btn-outline'}`}
              onClick={() => setShowDeficitOnly(!showDeficitOnly)}
              disabled={deficitCount === 0}
              title={deficitCount > 0 ? 'Mostrar solo productos con stock negativo' : 'No hay productos en déficit'}
            >
              <AlertTriangle size={20} />
              Requiere Corrección {deficitCount > 0 && `(${deficitCount})`}
            </button>
            
            {(filters.search || filters.boxType !== 'all' || showDeficitOnly) && (
              <button
                className="btn btn-outline"
                onClick={() => {
                  handleClearFilters();
                  setShowDeficitOnly(false);
                }}
              >
                <X size={20} />
                {t('common.clear')}
              </button>
            )}
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="advanced-filters">
            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label">Tipo de Caja</label>
                <select
                  className="form-select"
                  value={filters.boxType}
                  onChange={(e) => handleFilterChange('boxType', e.target.value)}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="M">M (Media)</option>
                  <option value="Q">Q (Quarter)</option>
                  <option value="P">P (Pequeña)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="card error-card">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p className="text-gray-600 mt-4">{t('common.loading')}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>{t('inventory.noData')}</h3>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('inventory.product')}</th>
                  <th>{t('inventory.quantity')}</th>
                  <th>
                    {filters.location === 'Campo' 
                      ? t('inventory.planned')
                      : filters.location === 'Warehouse USA'
                      ? t('inventory.inTransit')
                      : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span>EN TRÁNSITO</span>
                          <span>PLANIFICADO</span>
                        </div>
                      )}
                  </th>
                  <th>{t('inventory.location')}</th>
                  <th>{t('inventory.entryDate')}</th>
                  <th>{t('inventory.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="inventory-info">
                        <div className="inventory-icon">
                          <Package size={20} />
                        </div>
                        <div>
                          <div className="font-medium">{item.productos?.nombre || '—'}</div>
                          <div className="text-sm text-gray-500">{item.productos?.code || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="quantity-cell">
                        <span className={`inventory-quantity inventory-quantity-${getStockStatus(item.cantidad)}`}>
                          {item.cantidad}
                        </span>
                        {item.cantidad < 0 && (
                          <span className="deficit-badge" title="Esto indica que faltan registrar eventos de entrada (AGREGAR). Use el botón Corregir para agregar las entradas faltantes.">
                            <AlertTriangle size={14} />
                            DÉFICIT
                          </span>
                        )}
                        {item.cantidad === 0 && (
                          <span className="empty-badge">
                            SIN STOCK
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {item.ubicacion === 'Campo' ? (
                        <span className="inventory-planned">
                          {item.cantidad_planificada || 0}
                        </span>
                      ) : item.ubicacion === 'Warehouse USA' ? (
                        <span className="inventory-in-transit">
                          {item.cantidad_en_transito || 0}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td>
                      <div className="location-chip">
                        <MapPin size={14} />
                        <span>{item.ubicacion || '—'}</span>
                      </div>
                    </td>
                    <td>{formatDate(item.fecha_entrada)}</td>
                    <td>
                      {item.ubicacion === 'Campo' ? (
                        <div className="action-buttons">
                          {item.cantidad < 0 ? (
                            <Can I="add" this="inventory:management">
                              <button
                                className="btn-icon btn-icon-correct"
                                onClick={() => handleCorrect(item)}
                                title="Corregir déficit de inventario"
                              >
                                <AlertTriangle size={18} />
                              </button>
                            </Can>
                          ) : (
                            <>
                              <Can I="add" this="inventory:management">
                                <button
                                  className="btn-icon btn-icon-add"
                                  onClick={() => handleAdd(item)}
                                  title={t('inventory.addButton')}
                                >
                                  <PackagePlus size={18} />
                                </button>
                              </Can>
                              <Can I="subtract" this="inventory:management">
                                <button
                                  className="btn-icon btn-icon-reduce"
                                  onClick={() => handleReduce(item)}
                                  title={t('inventory.reduceButton')}
                                >
                                  <PackageMinus size={18} />
                                </button>
                              </Can>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddInventoryModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedStock(null);
        }}
        onSuccess={() => {
          dispatch(fetchInventory());
          setSelectedStock(null);
        }}
        preselectedItem={selectedStock}
      />

      <ReduceInventoryModal
        isOpen={isReduceModalOpen}
        onClose={() => {
          setIsReduceModalOpen(false);
          setSelectedStock(null);
        }}
        onSuccess={() => {
          dispatch(fetchInventory());
          setSelectedStock(null);
        }}
        preselectedItem={selectedStock}
      />

      <CorrectDeficitModal
        isOpen={isCorrectDeficitModalOpen}
        onClose={() => {
          setIsCorrectDeficitModalOpen(false);
          setSelectedStock(null);
        }}
        onSuccess={() => {
          dispatch(fetchInventory());
          setSelectedStock(null);
        }}
        item={selectedStock}
      />

      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSuccess={() => {
          dispatch(fetchInventory());
        }}
      />

      <PrintInventoryModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        items={items}
        currentLocation={filters.location}
      />

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--spacing-6);
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .btn-bulk-upload {
          padding: var(--spacing-2) var(--spacing-3);
          min-width: auto;
        }

        .btn-bulk-upload svg {
          margin: 0;
        }

        .btn-warning {
          background-color: #FEF3C7;
          color: #D97706;
          border-color: #FCD34D;
        }

        .btn-warning:hover:not(:disabled) {
          background-color: #FDE68A;
          border-color: #FBBF24;
        }

        .btn-warning.active {
          background-color: #F59E0B;
          color: white;
          border-color: #D97706;
        }

        .btn-warning:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .deficit-summary-card {
          display: flex;
          align-items: center;
          gap: var(--spacing-4);
          padding: var(--spacing-4);
          background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
          border: 2px solid #F59E0B;
          border-radius: var(--radius-lg);
          margin-bottom: var(--spacing-6);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .deficit-summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px -12px rgba(245, 158, 11, 0.5);
          border-color: #D97706;
        }

        .deficit-summary-icon {
          width: 60px;
          height: 60px;
          background-color: #F59E0B;
          color: white;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .deficit-summary-content {
          flex: 1;
        }

        .deficit-summary-title {
          font-size: var(--font-size-xl);
          font-weight: 700;
          color: #92400E;
          margin-bottom: var(--spacing-1);
        }

        .deficit-summary-subtitle {
          font-size: var(--font-size-base);
          color: #B45309;
          margin-bottom: var(--spacing-2);
        }

        .deficit-summary-action {
          font-size: var(--font-size-sm);
          color: #D97706;
          font-weight: 600;
        }

        .location-selector-card {
          margin-bottom: var(--spacing-6);
        }

        .location-selector-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          margin-bottom: var(--spacing-4);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .location-selector-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: var(--spacing-3);
        }

        .location-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          background: white;
          text-align: left;
          transition: all 0.2s ease-in-out;
          cursor: pointer;
        }

        .location-button:hover {
          border-color: var(--primary-blue);
          box-shadow: 0 10px 30px -12px rgba(59, 130, 246, 0.4);
        }

        .location-button.active {
          border-color: var(--primary-blue);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02));
        }

        .location-button-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background-color: rgba(59, 130, 246, 0.12);
          color: var(--primary-blue);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .location-button.active .location-button-icon {
          background-color: var(--primary-blue);
          color: white;
        }

        .location-button-text {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }

        .location-label {
          font-weight: 600;
          color: var(--gray-900);
          font-size: var(--font-size-lg);
        }

        .location-description {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }

        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-4);
          margin-bottom: var(--spacing-4);
        }

        .search-bar {
          position: relative;
          flex: 1;
        }

        .search-bar svg {
          position: absolute;
          left: var(--spacing-3);
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400);
        }

        .search-input {
          width: 100%;
          padding: var(--spacing-3) var(--spacing-3) var(--spacing-3) var(--spacing-10);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: var(--font-size-base);
          transition: border-color 0.2s ease-in-out;
        }

        .search-input:focus {
          outline: none;
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgb(59 130 246 / 0.1);
        }

        .filter-actions {
          display: flex;
          gap: var(--spacing-2);
        }

        .btn.active {
          background-color: var(--primary-blue);
          color: white;
        }

        .advanced-filters {
          padding-top: var(--spacing-4);
          border-top: 1px solid var(--gray-200);
        }

        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--spacing-4);
        }

        .error-card {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          background-color: #FEF2F2;
          color: #B91C1C;
          border: 1px solid #FECACA;
          margin-bottom: var(--spacing-4);
        }

        .table-container {
          overflow-x: auto;
        }

        th {
          text-align: left;
        }

        .inventory-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }

        .inventory-icon {
          width: 40px;
          height: 40px;
          background-color: var(--secondary-green);
          color: white;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quantity-cell {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
        }

        .inventory-quantity {
          font-weight: 600;
          color: var(--primary-blue);
          font-size: var(--font-size-lg);
        }

        .inventory-quantity-deficit {
          color: #DC2626 !important;
          font-weight: 700;
        }

        .inventory-quantity-empty {
          color: #F59E0B !important;
        }

        .deficit-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background-color: #FEE2E2;
          color: #DC2626;
          padding: 2px 8px;
          border-radius: var(--radius-md);
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: help;
        }

        .empty-badge {
          display: inline-flex;
          align-items: center;
          background-color: #FEF3C7;
          color: #D97706;
          padding: 2px 8px;
          border-radius: var(--radius-md);
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .inventory-in-transit {
          font-weight: 600;
          color: #F59E0B;
          font-size: var(--font-size-lg);
        }

        .inventory-planned {
          font-weight: 600;
          color: #8B5CF6;
          font-size: var(--font-size-lg);
        }

        .location-chip {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-1);
          background-color: var(--gray-100);
          color: var(--gray-700);
          padding: var(--spacing-1) var(--spacing-2);
          border-radius: var(--radius-full);
          font-size: var(--font-size-sm);
        }

        .action-buttons {
          display: flex;
          gap: var(--spacing-2);
          align-items: center;
        }

        .btn-icon {
          background: none;
          border: none;
          padding: var(--spacing-2);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-icon-add {
          color: #10B981;
        }

        .btn-icon-add:hover {
          background-color: rgba(16, 185, 129, 0.1);
        }

        .btn-icon-reduce {
          color: #F59E0B;
        }

        .btn-icon-reduce:hover {
          background-color: rgba(245, 158, 11, 0.1);
        }

        .btn-icon-correct {
          color: #F59E0B;
          background-color: #FEF3C7;
        }

        .btn-icon-correct:hover {
          background-color: #FDE68A;
          color: #D97706;
        }

        .empty-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }

        .empty-icon {
          font-size: 3rem;
        }

        .loading-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--gray-200);
          border-top: 4px solid var(--primary-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-4);
          }

          .location-selector-buttons {
            grid-template-columns: 1fr;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-grid {
            grid-template-columns: 1fr;
          }

          .table {
            font-size: var(--font-size-sm);
          }

          .inventory-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-2);
          }
        }
      `}</style>
    </div>
  );
};

export default Inventory;
